function nextId(prefix) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`;
}

export function createMetricRow(overrides = {}) {
  return {
    id: nextId("met"),
    enabled: true,
    sourceType: "field",
    field: "",
    expression: "",
    agg: "SUM",
    alias: "",
    format: "auto",
    percentile: 50,
    filter: null,
    sort: "",
    ...overrides,
  };
}

export function normalizeMetric(metric) {
  return {
    ...createMetricRow(),
    ...metric,
    filter: metric.filter?.field ? metric.filter : null,
  };
}

export function metricIsComplete(metric) {
  const m = normalizeMetric(metric);
  if (m.enabled === false) return false;
  if (m.sourceType === "expression") return Boolean(m.expression?.trim());
  if (m.agg === "COUNT") return true;
  return Boolean(m.field);
}

export function metricSource(metric) {
  const m = normalizeMetric(metric);
  if (m.sourceType === "expression") return m.expression.trim();
  if (m.agg === "COUNT" && !m.field) return "*";
  return m.field;
}

export function metricDefaultAlias(metric) {
  const m = normalizeMetric(metric);
  const src = metricSource(m);
  const safe = src === "*" ? "rows" : src.replace(/[^a-zA-Z0-9_]/g, "_");
  return `${m.agg.toLowerCase()}_${safe}`.slice(0, 48);
}

export function metricOutputName(metric) {
  const m = normalizeMetric(metric);
  return m.alias?.trim() || metricDefaultAlias(m);
}

export function metricDisplayLabel(metric) {
  const name = metricOutputName(metric);
  return name.replace(/_/g, " ");
}

function formatMetricFilter(filter) {
  if (!filter?.field) return "";
  const { field, op, value } = filter;
  if (op === "null") return `${field} IS NULL`;
  if (op === "notNull") return `${field} IS NOT NULL`;
  if (value === "" || value == null) return "";
  const quoted = /^-?\d+(\.\d+)?$/.test(String(value).trim()) ? value : `'${value}'`;
  return `${field} ${op} ${quoted}`;
}

export function formatMetricSql(metric) {
  const m = normalizeMetric(metric);
  if (!metricIsComplete(m)) return "";

  const src = metricSource(m);
  const alias = metricOutputName(m);

  let core;
  if (m.agg === "COUNT_DISTINCT") {
    core = `COUNT(DISTINCT ${src})`;
  } else if (m.agg === "PERCENTILE") {
    core = `PERCENTILE_CONT(${m.percentile / 100}) WITHIN GROUP (ORDER BY ${src})`;
  } else if (m.agg === "COUNT" && src === "*") {
    core = "COUNT(*)";
  } else {
    core = `${m.agg}(${src})`;
  }

  const filterSql = formatMetricFilter(m.filter);
  if (filterSql) core = `${core} FILTER (WHERE ${filterSql})`;

  return `${core} AS ${alias}`;
}

export function formatMetricValue(raw, metric) {
  const m = normalizeMetric(metric);
  const num = Number.parseFloat(raw);
  if (Number.isNaN(num)) return raw;

  switch (m.format) {
    case "integer":
      return Math.round(num).toLocaleString();
    case "decimal":
      return num.toFixed(2);
    case "percent":
      return `${(num * 100).toFixed(1)}%`;
    case "currency":
      return `₹${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    case "compact":
      if (Math.abs(num) >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
      if (Math.abs(num) >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
      return num.toFixed(1);
    default:
      return Number.isInteger(num) ? String(num) : num.toFixed(2);
  }
}

export function buildMetricsOrderBy(metrics) {
  const sorted = metrics
    .map(normalizeMetric)
    .filter((m) => m.sort && metricIsComplete(m))
    .sort((a, b) => {
      if (a.sort === b.sort) return 0;
      return a.sort === "desc" ? -1 : 1;
    });
  if (!sorted.length) return "";
  return `ORDER BY ${sorted.map((m) => `${metricOutputName(m)} ${m.sort.toUpperCase()}`).join(", ")}`;
}

export function createMetricFromPreset(preset, fieldNames = []) {
  const field = preset.field && fieldNames.includes(preset.field) ? preset.field : preset.field ?? "";
  return createMetricRow({
    sourceType: preset.sourceType ?? "field",
    field,
    expression: preset.expression ?? "",
    agg: preset.agg,
    alias: preset.alias ?? "",
    format: preset.format ?? "auto",
  });
}
